import { useState } from "react";
import { apiFetch, ApiClientError } from "../../lib/apiClient";
import type { DisplayNameType, DisplayPermission } from "../../types";

const MAX_LENGTH = 1000;

const PERMISSION_OPTIONS: { value: DisplayPermission; label: string }[] = [
  { value: "allow_with_name", label: "氏名・会社名つきで表示してよい" },
  { value: "allow_anonymous", label: "匿名なら表示してよい" },
  { value: "do_not_display", label: "表示しないでほしい" },
];

const NAME_TYPE_OPTIONS: { value: DisplayNameType; label: string }[] = [
  { value: "real_name", label: "氏名 + 会社名" },
  { value: "company_only", label: "会社名のみ" },
  { value: "anonymous", label: "匿名" },
];

export function QuestionForm({ registrationToken }: { registrationToken: string }) {
  const [questionText, setQuestionText] = useState("");
  const [permission, setPermission] = useState<DisplayPermission>("allow_anonymous");
  const [nameType, setNameType] = useState<DisplayNameType>("anonymous");
  const [wantsConsultation, setWantsConsultation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (questionText.trim().length === 0) {
      setError("質問内容を入力してください");
      return;
    }

    setSubmitting(true);
    try {
      await apiFetch<{ question_id: string }>("/api/public/questions", {
        method: "POST",
        body: JSON.stringify({
          registration_token: registrationToken,
          question_text: questionText,
          display_permission: permission,
          display_name_type: nameType,
          wants_consultation: wantsConsultation,
        }),
      });
      setDone(true);
    } catch (err) {
      const msg =
        err instanceof ApiClientError ? err.message : "送信に失敗しました";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="question-form question-form--done" role="status">
        <p>質問を受け付けました。すべての質問に回答できない場合があります。</p>
        <p>配信画面への表示は運営側で確認後に行います。</p>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            setDone(false);
            setQuestionText("");
            setWantsConsultation(false);
          }}
        >
          続けて質問する
        </button>
      </div>
    );
  }

  return (
    <form className="question-form" onSubmit={handleSubmit}>
      <h2>質問する</h2>

      <label className="field">
        <span className="field-label">質問内容</span>
        <textarea
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          maxLength={MAX_LENGTH}
          rows={5}
          required
          placeholder="質問内容を入力してください"
        />
        <span className="char-count">
          {questionText.length} / {MAX_LENGTH}
        </span>
      </label>

      <fieldset className="field">
        <legend>この質問を配信画面で紹介してもよいですか？</legend>
        {PERMISSION_OPTIONS.map((opt) => (
          <label key={opt.value} className="radio">
            <input
              type="radio"
              name="display_permission"
              value={opt.value}
              checked={permission === opt.value}
              onChange={() => setPermission(opt.value)}
            />
            {opt.label}
          </label>
        ))}
      </fieldset>

      <fieldset className="field">
        <legend>表示名</legend>
        {NAME_TYPE_OPTIONS.map((opt) => (
          <label key={opt.value} className="radio">
            <input
              type="radio"
              name="display_name_type"
              value={opt.value}
              checked={nameType === opt.value}
              onChange={() => setNameType(opt.value)}
            />
            {opt.label}
          </label>
        ))}
      </fieldset>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={wantsConsultation}
          onChange={(e) => setWantsConsultation(e.target.checked)}
        />
        個別相談を希望する
      </label>

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}

      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? "送信中..." : "質問を送信する"}
      </button>
    </form>
  );
}
